// src/lib/posthog/sanitize.ts
//
// URL redaction for the `before_send` hook. `sanitize_properties` is
// deprecated in posthog-js 1.424.0 and logs an error on every capture, so
// redaction happens here instead. Pure module: no `window`, no network, safe
// to call from both the browser SDK and any server capture.

import type { BeforeSendFn, Properties } from "posthog-js";

import { referrerOrigin } from "./attribution";

const REDACTED = ":redacted";

// The ten query keys whose values never reach PostHog.
const SENSITIVE_QUERY_KEYS = [
  "token",
  "code",
  "secret",
  "password",
  "email",
  "invitation",
  "apikey",
  "api_key",
  "signature",
  "access_token",
];

// Dynamic route segments: the id/token part is replaced, the rest of
// the path (including any prefix like `/api` or suffix like `/verify` on a
// nested value) is kept.
const DYNAMIC_SEGMENT_PATTERNS = [
  /(\/ramping\/pay\/)[^/?#]+/,
  /(\/staff\/accept\/)[^/?#]+/,
  /(\/paylink\/)[^/?#]+/,
];

// These pages pass their reset/setup token in the query string; the whole
// query is replaced rather than relying on a single known key name.
const FULL_QUERY_REDACT_PATH_PREFIXES = ["/reset-password", "/set-password"];

function redactDynamicSegments(value: string): string {
  return DYNAMIC_SEGMENT_PATTERNS.reduce(
    (acc, pattern) => acc.replace(pattern, `$1${REDACTED}`),
    value,
  );
}

// Strips scheme + host from an absolute URL, leaving just the path. A value
// with no scheme (a bare pathname, e.g. `$pathname`) is returned unchanged.
function extractPathname(value: string): string {
  const schemeMatch = value.match(/^[a-z][a-z0-9+.-]*:\/\//i);
  if (!schemeMatch) return value;
  const afterScheme = value.slice(schemeMatch[0].length);
  const pathStart = afterScheme.indexOf("/");
  return pathStart === -1 ? "" : afterScheme.slice(pathStart);
}

// A malformed percent-escape (e.g. `%E0=1`) must not throw: posthog-js drops
// the whole event when `before_send` throws, which would lose a pageview.
function safeDecode(raw: string): string {
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
}

// Redacts only the value of each sensitive key, leaving every other key,
// value and the original ordering untouched (no re-encoding).
function redactQuery(query: string): string {
  return query
    .split("&")
    .map((part) => {
      const eqIndex = part.indexOf("=");
      const rawKey = eqIndex === -1 ? part : part.slice(0, eqIndex);
      const key = safeDecode(rawKey.replace(/\+/g, " ")).toLowerCase();
      return SENSITIVE_QUERY_KEYS.includes(key) ? `${rawKey}=${REDACTED}` : part;
    })
    .join("&");
}

/**
 * Redacts the sensitive parts of a URL (absolute or a bare pathname): the
 * ten known query keys' values, the whole query string on `/reset-password*`
 * and `/set-password*`, and the dynamic id/token segment of `/ramping/pay/*`,
 * `/staff/accept/*` and `/paylink/*` — wherever that segment appears in the
 * string, so a nested value like `/api/paylink/<id>/verify` is caught too.
 */
export function redactUrl(raw: string): string {
  if (raw.length === 0) return raw;

  const queryIndex = raw.indexOf("?");
  const base = redactDynamicSegments(
    queryIndex === -1 ? raw : raw.slice(0, queryIndex),
  );
  if (queryIndex === -1) return base;

  const query = raw.slice(queryIndex + 1);
  const pathname = extractPathname(base);
  const redactedQuery = FULL_QUERY_REDACT_PATH_PREFIXES.some((prefix) =>
    pathname.startsWith(prefix),
  )
    ? REDACTED
    : redactQuery(query);

  return `${base}?${redactedQuery}`;
}

function looksLikeUrl(value: string): boolean {
  return value.startsWith("/") || /^https?:\/\//i.test(value);
}

function redactPropertyValue(value: unknown): unknown {
  if (typeof value === "string") {
    return looksLikeUrl(value) ? redactUrl(value) : value;
  }
  if (Array.isArray(value)) {
    return value.map(redactPropertyValue);
  }
  if (value !== null && typeof value === "object") {
    const result: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(value as Record<string, unknown>)) {
      result[key] = redactPropertyValue(val);
    }
    return result;
  }
  return value;
}

function redactProperties(properties: Properties): Properties {
  return redactPropertyValue(properties) as Properties;
}

// posthog-js's own referrer keys are reduced to their origin, on top of the
// redaction every other property gets: a referring page's query string
// (e.g. an order number) must not leave the browser.
// `$session_entry_referrer` is posthog-js's per-session copy of `$referrer`
// (`getSessionProps` prefixes the initial props), sent on every event.
// `$direct` (no referrer) is left as is; anything else that does not parse to
// an http(s) origin becomes `null`.
const REFERRER_KEYS = ["$referrer", "$initial_referrer", "$session_entry_referrer"] as const;

function reduceReferrerKeys(properties: Properties): Properties {
  const result: Properties = { ...properties };
  for (const key of REFERRER_KEYS) {
    const value: unknown = result[key];
    if (typeof value === "string" && value !== "$direct") {
      result[key] = referrerOrigin(value);
    }
  }
  return result;
}

// posthog-js keys `$heatmap_data` by the raw page URL (`{ [url]: points[] }`),
// and value redaction never touches object keys, so each key goes through
// `redactUrl` here. Two keys that redact to the same URL (two emails on
// `/2fa/verify?email=`) have their point arrays concatenated in order.
function redactHeatmapKeys(properties: Properties): Properties {
  const data: unknown = properties.$heatmap_data;
  if (data === null || typeof data !== "object" || Array.isArray(data)) {
    return properties;
  }
  const merged: Record<string, unknown> = {};
  for (const [url, points] of Object.entries(data as Record<string, unknown>)) {
    const key = redactUrl(url);
    const prev = merged[key];
    merged[key] = Array.isArray(prev) && Array.isArray(points) ? [...prev, ...points] : points;
  }
  return { ...properties, $heatmap_data: merged };
}

// Events whose properties carry the text and attributes of the element the
// user interacted with.
const ELEMENT_EVENTS = new Set(["$autocapture", "$rageclick", "$dead_click"]);

// The human-readable attributes that can hold a name, an email or a label.
// `data-value` is where cmdk option items keep their label.
const HUMAN_ATTRIBUTES = ["title", "alt", "aria-label", "placeholder", "data-value"];

const ELEMENT_TEXT_MASK = "***";

// PostHog's chain parser cannot read an EMPTY quoted value (it swallows the
// segment's trailing `nth-child`/`nth-of-type`), so the replacement must stay
// non-empty.
// The chain is one long string of `tag.class:attr__x="v"text="…"` segments, so
// the quoted values are matched with their escapes: `[^"]*` would stop at the
// first \" and leave the rest of the text in place.
const QUOTED_VALUE = /(?:\\.|[^"\\])*/.source;
const TEXT_IN_CHAIN_RE = new RegExp(`text="${QUOTED_VALUE}"`, "g");
const HUMAN_ATTR_IN_CHAIN_RE = new RegExp(
  `(attr__(?:${HUMAN_ATTRIBUTES.join("|")})=)"${QUOTED_VALUE}"`,
  "gi",
);
// posthog-js writes a link twice in the chain: `attr__href="…"` and a bare `href="…"`.
const HREF_IN_CHAIN_RE = new RegExp(`((?:attr__)?href=)"(${QUOTED_VALUE})"`, "g");

/**
 * Autocapture, rage clicks and dead clicks ship the clicked element's own text
 * as `$el_text` and again inside `$elements_chain`, next to its attributes.
 * Both are removed or masked here; what survives is the selector chain (tag,
 * classes, nth-child) that heatmaps and click maps need. Any other event is
 * returned as is, and a value of an unexpected shape is skipped rather than
 * thrown on: posthog-js drops the event when `before_send` throws.
 */
function stripElementText(event: string, properties: Properties): Properties {
  if (!ELEMENT_EVENTS.has(event)) return properties;
  try {
    const { $el_text: _elText, ...rest } = properties;
    const result: Properties = { ...rest };
    if (typeof result.$elements_chain === "string") {
      result.$elements_chain = result.$elements_chain
        .replace(TEXT_IN_CHAIN_RE, `text="${ELEMENT_TEXT_MASK}"`)
        .replace(HUMAN_ATTR_IN_CHAIN_RE, `$1"${ELEMENT_TEXT_MASK}"`)
        .replace(HREF_IN_CHAIN_RE, (_m, name: string, href: string) => `${name}"${redactUrl(href)}"`);
    }
    if (Array.isArray(result.$elements)) {
      result.$elements = result.$elements.map((el: unknown) => {
        if (el === null || typeof el !== "object") return el;
        const {
          $el_text: _text,
          text: _plain,
          ...entry
        } = el as Record<string, unknown>;
        for (const attr of HUMAN_ATTRIBUTES) delete entry[`attr__${attr}`];
        return entry;
      });
    }
    return result;
  } catch {
    return properties;
  }
}

/**
 * The `before_send` hook itself: redacts `$current_url`, `$referrer`,
 * `$pathname` and any other nested property whose value is a URL, before the
 * event leaves the browser. posthog-js puts `$set` / `$set_once` at
 * the top level of the capture result (e.g. `$initial_current_url`,
 * `$initial_referrer` on the first event and on `$identify`), so those person
 * properties are redacted the same way. `$heatmap_data` is keyed by page URL,
 * so its keys are redacted as well. `$referrer`, `$initial_referrer` and
 * `$session_entry_referrer` are further reduced to their origin.
 */
export const redactEvent: BeforeSendFn = (cr) => {
  if (cr === null) return null;
  return {
    ...cr,
    properties: reduceReferrerKeys(
      redactProperties(redactHeatmapKeys(stripElementText(cr.event, cr.properties))),
    ),
    ...(cr.$set ? { $set: reduceReferrerKeys(redactProperties(cr.$set)) } : {}),
    ...(cr.$set_once ? { $set_once: reduceReferrerKeys(redactProperties(cr.$set_once)) } : {}),
  };
};
